import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Push-notification action consumers — `?resolve=<id>` (admin) and
 * `?safe=<id>` (citizen). Each fires the corresponding API call exactly
 * once and strips its own param from the URL so a refresh cannot replay
 * the action. The `consumedActionRef` is keyed `<verb>:<id>` so the same
 * alert can later be resolved AND have a safe-check-in if needed.
 */
export function usePushAction({ searchParams, setSearchParams, isAdmin, userLocation, fetchAlerts, fetchStats }) {
  const consumedActionRef = useRef(null);

  useEffect(() => {
    const resolveId = searchParams.get("resolve");
    const safeId = searchParams.get("safe");
    if (!resolveId && !safeId) return;

    const verb = resolveId ? "resolve" : "safe";
    const id = resolveId || safeId;
    const memo = `${verb}:${id}`;
    if (consumedActionRef.current === memo) return;
    consumedActionRef.current = memo;

    const stripParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete(verb);
      setSearchParams(next, { replace: true });
    };

    (async () => {
      if (verb === "resolve") {
        if (!isAdmin) {
          toast.error("Admins only — cannot resolve from this account");
          stripParam();
          return;
        }
        try {
          await axios.patch(`${API}/alerts/${id}/resolve`, {}, { withCredentials: true });
          toast.success("Alert resolved from notification");
          fetchAlerts();
          fetchStats();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not resolve alert");
        } finally {
          stripParam();
        }
      } else {
        // I'm-safe check-in: try GPS first; fall back to last known location
        let lat, lng;
        if (userLocation) {
          lat = userLocation.latitude;
          lng = userLocation.longitude;
        } else if (navigator.geolocation) {
          try {
            const pos = await new Promise((res, rej) =>
              navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 }),
            );
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
          } catch {
            // Allow check-in without coords as a last resort — server stores 0,0
            lat = 0;
            lng = 0;
          }
        }
        try {
          await axios.post(
            `${API}/alerts/${id}/safe-check-in`,
            { latitude: lat ?? 0, longitude: lng ?? 0 },
            { withCredentials: true },
          );
          toast.success("Marked yourself safe — admins notified");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not record check-in");
        } finally {
          stripParam();
        }
      }
    })();
  }, [searchParams, setSearchParams, isAdmin, userLocation, fetchAlerts, fetchStats]);
}
